/**
 * The voices the bot can speak with (ENG-3738 / ENG-3473). One catalog, in code: ElevenLabs id,
 * the name operators see, and which one is the platform default. A Twilio number picks one of
 * these in Settings › Apps › Twilio ("Voice"); a number with no pick, or a saved id that is no
 * longer in the catalog, speaks with the default. There is no environment variable for the voice
 * (Chuck, 2026-11-02): the shipped voice is the code default, and a per-number pick is a setting.
 */
export interface BotVoice {
  /** ElevenLabs voice id. */
  id: string;
  /** What operators see in the picker. Never rendered into a prompt. */
  displayName: string;
  /** Exactly one voice in the catalog is the default. */
  isDefault?: boolean;
}

export const BOT_VOICES: BotVoice[] = [
  { id: "EST9Ui6982FZPSi7gCHi", displayName: "Elise", isDefault: true },
  { id: "DODLEQrClDo8wCz460ld", displayName: "Lauren" },
  { id: "g6xIsTj2HwM6VR4iXFCw", displayName: "Jessica" }
];

export function defaultBotVoice(): BotVoice {
  return BOT_VOICES.find((voice) => voice.isDefault) ?? BOT_VOICES[0];
}

export function findBotVoice(id: string | null | undefined): BotVoice | undefined {
  const wanted = (id ?? "").trim();
  return wanted ? BOT_VOICES.find((voice) => voice.id === wanted) : undefined;
}

export interface ResolvedBotVoice {
  voice: BotVoice;
  /** `number` = the Twilio app's own pick; `default` = nothing saved, or a saved id not in the catalog. */
  source: "number" | "default";
  /** Set when the number had a saved id the catalog doesn't know; the caller logs it for admins. */
  unknownId?: string;
}

/** The voice a number speaks with: its saved pick when the catalog knows it, else the default. */
export function resolveBotVoice(saved: string | null | undefined): ResolvedBotVoice {
  const picked = findBotVoice(saved);
  if (picked) return { voice: picked, source: "number" };
  const trimmed = (saved ?? "").trim();
  return trimmed ? { voice: defaultBotVoice(), source: "default", unknownId: trimmed } : { voice: defaultBotVoice(), source: "default" };
}
